import {animate, state, style, transition, trigger} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {IndividualConfig, ToastPackage, ToastrService} from 'ngx-toastr';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SafeHtml} from '@angular/platform-browser';

@Component({
  selector: 'app-system-message',
  templateUrl: './system-message.component.html',
  styleUrls: ['./system-message.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('flyInOut', [
      state('inactive', style({opacity: 0})),
      state('active', style({opacity: 1})),
      state('removed', style({opacity: 0})),
      transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}'))
    ])
  ]
})
export class SystemMessageComponent implements OnDestroy {
  message?: string | null | SafeHtml;
  title?: string;
  options: IndividualConfig;
  duplicatesCount!: number;
  originalTimeout: number;
  /** width of progress bar */
  width = -1;
  private timeout: any;
  intervalId: any;
  private hideTime!: number;
  private ngUnsubscribe = new Subject<void>();
  @ViewChild('main') mainElement;
  /** controls animation */
  @HostBinding('@flyInOut') public state;
  constructor(
    protected toastrService: ToastrService,
    private toastPackage: ToastPackage,
    private cd: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.message = toastPackage.message;
    this.title = toastPackage.title;
    this.options = toastPackage.config;
    this.originalTimeout = toastPackage.config.timeOut;

    toastPackage.toastRef
      .afterActivate()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.activateToast();
      });
    toastPackage.toastRef
      .manualClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.remove();
      });
    toastPackage.toastRef
      .timeoutReset()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.resetTimeout();
      });
    toastPackage.toastRef
      .countDuplicate()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(count => {
        this.duplicatesCount = count;
      });

    this.state = {
      value: 'inactive',
      params: {
        easeTime: this.toastPackage.config.easeTime || '300',
        easing: 'ease-in'
      }
    };
  }

  @HostListener('mouseenter')
  stickAround(): void {
    if (this.state.value === 'removed') {
      return;
    }
    clearTimeout(this.timeout);
    this.options.timeOut = 0;
    this.hideTime = 0;

    // disable progressBar
    clearInterval(this.intervalId);
    this.width = 0;
  }
  @HostListener('mouseleave')
  delayedHideToast(): void {
    if (
      this.options.disableTimeOut === true ||
      this.options.disableTimeOut === 'extendedTimeOut' ||
      this.options.extendedTimeOut === 0 ||
      this.state.value === 'removed'
    ) {
      return;
    }
    this.outsideTimeout(() => this.remove(), this.options.extendedTimeOut);
    this.options.timeOut = this.options.extendedTimeOut;
    this.hideTime = new Date().getTime() + (this.options.timeOut || 0);
    this.width = -1;
    if (this.options.progressBar) {
      this.outsideInterval(() => this.updateProgress(), 10);
    }
  }

  outsideTimeout(func: () => any, timeout: number): void {
    if (this.ngZone) {
      this.ngZone.runOutsideAngular(() => (this.timeout = setTimeout(() => this.runInsideAngular(func), timeout)));
    } else {
      this.timeout = setTimeout(() => func(), timeout);
    }
  }

  outsideInterval(func: () => any, timeout: number): void {
    if (this.ngZone) {
      this.ngZone.runOutsideAngular(() => (this.intervalId = setInterval(() => this.runInsideAngular(func), timeout)));
    } else {
      this.intervalId = setInterval(() => func(), timeout);
    }
  }

  private runInsideAngular(func: () => any): void {
    if (this.ngZone) {
      this.ngZone.run(() => func());
    } else {
      func();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    clearInterval(this.intervalId);
    clearTimeout(this.timeout);
  }

  /**
   * activates toast and sets timeout
   */
  private activateToast(): void {
    this.state = {...this.state, value: 'active'};
    if (!(this.options.disableTimeOut === true || this.options.disableTimeOut === 'timeOut') && this.options.timeOut) {
      this.timeout = setTimeout(() => {
        this.remove();
      }, this.options.timeOut);
      this.hideTime = new Date().getTime() + this.options.timeOut;
      if (this.options.progressBar) {
        this.intervalId = setInterval(() => this.updateProgress(), 10);
      }
    }
  }
  /**
   * updates progress bar width
   */
  updateProgress(): void {
    if (this.width === 0 || this.width === 100 || !this.options.timeOut) {
      return;
    }
    const now = new Date().getTime();
    const remaining = this.hideTime - now;
    this.width = (remaining / this.options.timeOut) * 100;
    if (this.options.progressAnimation === 'increasing') {
      this.width = 100 - this.width;
    }
    if (this.width <= 0) {
      this.width = 0;
    }
    if (this.width >= 100) {
      this.width = 100;
    }
    this.cd.markForCheck();
  }

  resetTimeout(): void {
    clearTimeout(this.timeout);
    clearInterval(this.intervalId);
    this.state = {...this.state, value: 'active'};

    this.options.timeOut = this.originalTimeout;
    this.timeout = setTimeout(() => this.remove(), this.originalTimeout);
    this.hideTime = new Date().getTime() + (this.originalTimeout || 0);
    this.width = -1;
    if (this.options.progressBar) {
      this.intervalId = setInterval(() => this.updateProgress(), 10);
    }
  }

  /**
   * tells toastrService to remove this toast after animation time
   */
  remove(): void {
    (this.mainElement.nativeElement as HTMLDivElement).removeAttribute('role');
    (this.mainElement.nativeElement as HTMLDivElement).removeAttribute('aria-live');
    if (this.state.value === 'removed') {
      return;
    }
    clearTimeout(this.timeout);
    this.state = {...this.state, value: 'removed'};
    this.outsideTimeout(() => this.toastrService.remove(this.toastPackage.toastId), +this.toastPackage.config.easeTime);
  }
}
